import { Edit3, Plus, Search, ShieldCheck, Trash2, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import { deletePerson, savePerson } from '../../api';
import { EmptyState, Field, Modal, PersonAvatar, cx, fieldClass } from '../../components/ui';
import type { AuthSession, Person, PersonFormData } from '../../types';

function toForm(person?: Person): PersonFormData {
  return {
    id: person?.id,
    name: person?.name ?? '',
    courtesyName: person?.courtesyName ?? '',
    generation: person?.generation ?? 18,
    branch: person?.branch ?? '长房',
    region: person?.region ?? '广西桂林',
    gender: person?.gender ?? '男',
    birthDate: person?.birth ?? '',
    deathDate: person?.death ?? '',
    origin: person?.origin ?? '广西桂林',
    biography: person?.biography ?? '',
    privacy: person?.privacy ?? 'family',
    status: person?.status ?? 'verified',
    parentIds: person?.parentIds ?? [],
    spouseIds: person?.spouseIds ?? [],
    childIds: person?.childIds ?? []
  };
}

function PersonEditor({ session, people, person, onClose, onSaved }: { session: AuthSession; people: Person[]; person?: Person; onClose: () => void; onSaved: (message: string) => Promise<void> | void }) {
  const [form, setForm] = useState(() => toForm(person));
  const [busy, setBusy] = useState(false);
  const candidates = people.filter((item) => item.id !== form.id);
  const update = <Key extends keyof PersonFormData>(key: Key, value: PersonFormData[Key]) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal title={person ? '编辑族人档案' : '新增族人档案'} subtitle={session.role === 'member' ? '修改将提交管理员审核' : '管理员修改即时生效'} onClose={onClose} size="wide">
      <form
        className="person-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setBusy(true);
          try {
            await savePerson(session, form);
            await onSaved(session.role === 'admin' ? '成员资料已保存' : '修改已提交审核');
            onClose();
          } finally {
            setBusy(false);
          }
        }}
      >
        <div className="form-grid three">
          <Field label="姓名"><input className={fieldClass} value={form.name} onChange={(event) => update('name', event.target.value)} required /></Field>
          <Field label="字辈 / 字"><input className={fieldClass} value={form.courtesyName ?? ''} onChange={(event) => update('courtesyName', event.target.value)} /></Field>
          <Field label="世代"><input className={fieldClass} type="number" min="1" value={form.generation} onChange={(event) => update('generation', Number(event.target.value))} required /></Field>
          <Field label="分支"><input className={fieldClass} value={form.branch} onChange={(event) => update('branch', event.target.value)} required /></Field>
          <Field label="常住地区"><input className={fieldClass} value={form.region} onChange={(event) => update('region', event.target.value)} required /></Field>
          <Field label="性别"><select className={fieldClass} value={form.gender} onChange={(event) => update('gender', event.target.value as '男' | '女')}><option>男</option><option>女</option></select></Field>
          <Field label="出生日期"><input className={fieldClass} value={form.birthDate} onChange={(event) => update('birthDate', event.target.value)} required /></Field>
          <Field label="去世日期"><input className={fieldClass} value={form.deathDate ?? ''} onChange={(event) => update('deathDate', event.target.value)} /></Field>
          <Field label="籍贯"><input className={fieldClass} value={form.origin} onChange={(event) => update('origin', event.target.value)} /></Field>
          <Field label="隐私级别"><select className={fieldClass} value={form.privacy} onChange={(event) => update('privacy', event.target.value as PersonFormData['privacy'])}><option value="public">公开</option><option value="family">家族可见</option><option value="private">私密</option></select></Field>
          <Field label="档案状态"><select className={fieldClass} value={form.status} onChange={(event) => update('status', event.target.value as PersonFormData['status'])}><option value="verified">已核验</option><option value="pending">待审核</option><option value="memorial">纪念</option></select></Field>
        </div>

        <section className="relation-editor">
          <div><strong>关系维护</strong><span>编辑子女档案并设置父母，可自动形成双向关系。</span></div>
          <div className="form-grid three">
            <Field label="父母一">
              <select className={fieldClass} value={form.parentIds[0] ?? ''} onChange={(event) => update('parentIds', [event.target.value, form.parentIds[1]].filter(Boolean))}>
                <option value="">未设置</option>{candidates.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.generation}世</option>)}
              </select>
            </Field>
            <Field label="父母二">
              <select className={fieldClass} value={form.parentIds[1] ?? ''} onChange={(event) => update('parentIds', [form.parentIds[0], event.target.value].filter(Boolean))}>
                <option value="">未设置</option>{candidates.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.generation}世</option>)}
              </select>
            </Field>
            <Field label="配偶">
              <select className={fieldClass} value={form.spouseIds[0] ?? ''} onChange={(event) => update('spouseIds', event.target.value ? [event.target.value] : [])}>
                <option value="">未设置</option>{candidates.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.generation}世</option>)}
              </select>
            </Field>
          </div>
        </section>

        <Field label="生平简介"><textarea className={cx(fieldClass, 'min-h-32 resize-y')} value={form.biography} onChange={(event) => update('biography', event.target.value)} /></Field>
        <footer className="modal-actions"><button type="button" className="button secondary" onClick={onClose}>取消</button><button type="submit" className="button primary" disabled={busy}>{busy ? '保存中' : '保存档案'}</button></footer>
      </form>
    </Modal>
  );
}

export function MemberWorkspace({ session, people, onClose, onChanged, onSelect }: { session: AuthSession; people: Person[]; onClose: () => void; onChanged: (message: string) => Promise<void>; onSelect: (person: Person) => void }) {
  const [query, setQuery] = useState('');
  const [branch, setBranch] = useState('all');
  const [editing, setEditing] = useState<Person | null | undefined>(undefined);
  const branches = useMemo(() => [...new Set(people.map((person) => person.branch))], [people]);
  const filtered = useMemo(() => people.filter((person) => {
    const matchesQuery = [person.name, person.courtesyName, person.origin, person.region].filter(Boolean).some((value) => value!.includes(query.trim()));
    const matchesBranch = branch === 'all' || person.branch === branch;
    return (!query.trim() || matchesQuery) && matchesBranch;
  }), [people, query, branch]);

  return (
    <Modal title="族人管理" subtitle={`${people.length} 位族人 · ${branches.length} 个分支`} onClose={onClose} size="wide">
      <div className="member-toolbar">
        <label className="search-control"><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="按姓名、字辈、地区搜索" /></label>
        <select className={fieldClass} value={branch} onChange={(event) => setBranch(event.target.value)}><option value="all">全部分支</option>{branches.map((item) => <option key={item}>{item}</option>)}</select>
        <button type="button" className="button primary" disabled={session.role === 'guest'} onClick={() => setEditing(null)}><Plus />新增成员</button>
      </div>

      <div className="member-list">
        {filtered.map((person) => (
          <article key={person.id} className="member-row">
            <button type="button" className="member-identity" onClick={() => { onSelect(person); onClose(); }}>
              <PersonAvatar person={person} />
              <span><strong>{person.name}</strong><small>{person.generation}世 · {person.branch} · {person.birth}{person.death ? `-${person.death}` : '-'}</small></span>
            </button>
            <span className="member-origin">{person.origin || person.region}</span>
            <span className={cx('status-chip', person.status)}><ShieldCheck />{person.status === 'verified' ? '已核验' : person.status === 'pending' ? '待审核' : '纪念档案'}</span>
            <div className="member-actions">
              <button type="button" className="icon-button" title="编辑" disabled={session.role === 'guest'} onClick={() => setEditing(person)}><Edit3 /></button>
              {session.role === 'admin' && <button type="button" className="icon-button danger" title="删除" onClick={async () => { if (!window.confirm(`确定删除 ${person.name}？此操作不可恢复。`)) return; await deletePerson(session, person.id); await onChanged('成员已删除'); }}><Trash2 /></button>}
            </div>
          </article>
        ))}
        {filtered.length === 0 && <EmptyState title="没有匹配成员" description="调整搜索词或分支筛选。" />}
      </div>

      <footer className="member-summary"><Users /><span>管理员可直接保存和删除；家族成员修改进入审核队列。</span></footer>
      {editing !== undefined && <PersonEditor session={session} people={people} person={editing ?? undefined} onClose={() => setEditing(undefined)} onSaved={onChanged} />}
    </Modal>
  );
}

export { PersonEditor };
